/**
 * 시딩 캠페인의 **글 그룹**(셀럽 · 인플루언서 …) — 캠페인 문서 `postGroups` 를 읽고, 그룹별로 묶고, 소계를 센다.
 *
 * 저장 모양: `postGroups: { [키]: 그룹 이름 }`. 키는 세 가지다 — 글 키 둘(`link:` + 정규화 링크 · ES id)과
 * 계정 키(`account:` + `campaignAccountKey`, `campaignAccountGroupKey`).
 * 🔑 읽는 차례(`campaignPostGroupOf`): 글의 링크 키 → 같은 글(`campaignLinkIdentity`)인 링크 키 → 글의 id 키 → 글쓴 계정 키(채널+계정 → 채널 없는 계정).
 *    링크 칸에 쇼츠로 넣은 링크의 그룹이 `watch?v=` 로 색인된 그 글에도 붙는 것은 둘째 차례 덕이다(저장 키는 그대로 둔다).
 *    그래서 계정에 그룹을 정하면 그 계정의 글은 나중에 모인 글까지 모두 그 그룹이고, 표에서 글 하나만 바꾸면 그 글만 다르다.
 *    캠페인 창 링크 칸과 표는 링크가 있는 글이면 링크 키 하나만 적는다(`campaignPostGroupKeys` — 강제추가 링크의 글은 그 등록 링크의 키) ·
 *    링크가 없는 글만 id 키를 적는다.
 * 🔑 값이 `CAMPAIGN_POST_GROUP_NONE`(`_none`)인 글 키는 «그룹을 일부러 뺀 글»이다 — 그 글은 계정 그룹을 물려받지 않고 「그룹 없음」이다.
 *    그래서 `_none` 과 `__…__` 꼴은 그룹 이름으로 쓸 수 없다(`campaignPostGroupNameError`).
 * 그룹 이름은 캠페인마다 자유 글이고, 고를 칸은 이 캠페인에서 쓴 이름뿐이다(미리 깔아 둔 그룹은 없다).
 * 소계 숫자는 상단 카드와 같은 셈(`summarizeCampaignPosts` · `summarizeCampaignReactionParts`)이다.
 */

import type { CampaignPost } from 'src/types/campaign/campaign-domain';

import { engagementRatePct } from 'src/utils/metrics/engagement-rate';

import { campaignAccountKey } from './campaign-accounts';
import { sanitizeCampaignLink } from './campaign-link-sanitize';
import { matchCampaignLink, campaignLinkIdentity } from './campaign-link-identity';
import { summarizeCampaignPosts, summarizeCampaignReactionParts } from './campaign-list-metrics';

import type { CampaignReactionParts } from './campaign-list-metrics';

/** 그룹 이름 최대 글자 수 — 서버도 같은 값으로 자른다 */
export const CAMPAIGN_POST_GROUP_NAME_MAX = 20;

/** 「그룹 없음」 묶음·거르기의 키 — 글 키에 저장되면 «그룹을 일부러 뺀 글»(계정 그룹을 물려받지 않는다)이다 */
export const CAMPAIGN_POST_GROUP_NONE = '_none';

/** 그룹 이름으로 쓸 수 없는 이름(`_none` · `__…__`)을 넣었을 때 */
export const CAMPAIGN_POST_GROUP_RESERVED_NAME = '쓸 수 없는 이름이에요';

/** 「그룹 없음」의 화면 말 */
export const CAMPAIGN_POST_GROUP_NONE_LABEL = '그룹 없음';

/** 그룹 칩 색 — 앱 라벨 팔레트(`Label` 의 `color`) 키만 쓴다 */
export type CampaignPostGroupColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'default';

/** 그룹이 받는 칩 색 — 이름으로 하나가 정해진다 */
const CUSTOM_GROUP_COLORS: readonly CampaignPostGroupColor[] = Object.freeze(['secondary', 'error', 'primary', 'info', 'success', 'warning']);

/**
 * 그룹 칩 색 — 같은 이름은 표 칸 · 묶음 머리 · 고르개 어디서나 같은 색이다.
 * @returns 그룹이 없으면 `default`(회색)
 */
export function campaignPostGroupColor(name: string | null | undefined): CampaignPostGroupColor {
  const key = normalizeCampaignPostGroupName(name);
  if (!key || key === CAMPAIGN_POST_GROUP_NONE) return 'default';
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 2147483647;
  return CUSTOM_GROUP_COLORS[hash % CUSTOM_GROUP_COLORS.length];
}

/** 그룹 거르기 — 전체 · 그룹 없음 · 그룹 이름 */
export type CampaignPostGroupFilter = 'all' | typeof CAMPAIGN_POST_GROUP_NONE | string;

/** 그룹 하나(또는 합계)의 숫자 */
export type CampaignPostGroupSummary = {
  postsCount: number;
  viewTotal: number;
  /** 좋아요+댓글+저장+공유+다운로드 — 상단 카드 「총 반응」과 같은 셈 */
  reactionTotal: number;
  parts: CampaignReactionParts;
  /** (좋아요+댓글) ÷ 조회 × 100 — 조회가 0 이면 `null` */
  engagementRate: number | null;
};

/** 묶음 하나 — `key` 는 그룹 이름 또는 `CAMPAIGN_POST_GROUP_NONE` */
export type CampaignPostGroupSection<T> = {
  key: string;
  name: string;
  posts: T[];
  summary: CampaignPostGroupSummary;
};

/**
 * 그룹 이름 다듬기 — 앞뒤 공백을 떼고 연속 공백을 하나로, 최대 글자 수로 자른다.
 * @returns 빈 문자열이면 「그룹 없음」으로 읽는다 · 쓸 수 없는 이름(`_none` · `__…__`)도 빈 문자열
 */
export function normalizeCampaignPostGroupName(raw: unknown): string {
  const name = collapseGroupName(raw);
  return isReservedGroupName(name) ? '' : name;
}

/**
 * 그룹 이름으로 쓸 수 있나 — 고르개 · 서버가 새 이름을 받기 전에 묻는다.
 * @returns 쓸 수 없으면 「쓸 수 없는 이름이에요」 · 쓸 수 있거나 비었으면 `null`
 */
export function campaignPostGroupNameError(raw: unknown): string | null {
  return isReservedGroupName(collapseGroupName(raw)) ? CAMPAIGN_POST_GROUP_RESERVED_NAME : null;
}

/** 링크로 넣은 글의 그룹 키 — 링크가 아니면 빈 문자열 */
export function campaignLinkGroupKey(link: unknown): string {
  const normalized = sanitizeCampaignLink(String(link ?? ''));
  return normalized ? `link:${normalized}` : '';
}

/** 계정 키 접두 — 글 키(ES id · `link:`)와 겹치지 않는다 */
export const CAMPAIGN_ACCOUNT_GROUP_KEY_PREFIX = 'account:';

/**
 * 계정의 그룹 키 — `account:` + 계정별 표가 글을 계정에 붙이는 열쇠(`campaignAccountKey`, 소문자 `채널*계정`).
 * @returns 계정 이름이 비면 빈 문자열
 */
export function campaignAccountGroupKey(platform: unknown, account: unknown): string {
  if (!String(account ?? '').trim()) return '';
  return `${CAMPAIGN_ACCOUNT_GROUP_KEY_PREFIX}${campaignAccountKey(platform, account)}`;
}

/** 글쓴 계정의 그룹 키들 — 채널+계정 → 채널 없는 계정(옛 이름만 적은 계정 줄) 차례 */
export function campaignPostAccountGroupKeys(post: any): string[] {
  const account = post?.account || post?.username || '';
  if (!String(account).trim()) return [];
  const platform = String(post?.source || post?.platform || '').trim();
  const keys = platform ? [campaignAccountGroupKey(platform, account)] : [];
  keys.push(campaignAccountGroupKey('', account));
  return keys;
}

/**
 * 표에서 글 하나의 그룹을 고칠 때 적는 키 — 링크가 있으면 링크 키 하나, 없으면 id 키 하나.
 * 강제추가 링크와 같은 글(`campaignLinkIdentity`)이면 그 **등록 링크**의 키다 — 캠페인 창 링크 칸이 같은 키를 읽고 지운다.
 * @param liveLinks 지금 등록된 강제추가 링크(`readForcedAdditionLiveLinks`) — 없으면 글 링크의 키
 * @returns 링크도 id 도 없으면 빈 배열
 */
export function campaignPostGroupKeys(post: any, liveLinks?: ReadonlySet<string>): string[] {
  const link = post?.link || post?.url || '';
  const forced = liveLinks && liveLinks.size > 0 ? matchCampaignLink(post, liveLinks) : null;
  const linkKey = campaignLinkGroupKey(forced || link);
  if (linkKey) return [linkKey];
  const id = String(post?.id || '').trim();
  return id ? [id] : [];
}

/**
 * 글 키를 새로 적을 때 함께 지울 키 — 그 글의 id 키 · 같은 글인 다른 링크 키 중 저장된 것. 남기면 읽는 차례상 그 값이 새 값을 가린다.
 * @param writeKeys 이번에 적는 키(`campaignPostGroupKeys`)
 * @param saved `readCampaignPostGroups` 결과
 */
export function staleCampaignPostGroupKeys(post: any, writeKeys: readonly string[], saved: ReadonlyMap<string, string>): string[] {
  const written = new Set(writeKeys);
  const out: string[] = [];
  const id = String(post?.id || '').trim();
  if (id && !written.has(id) && saved.has(id)) out.push(id);
  const identity = campaignLinkIdentity(String(post?.link || post?.url || ''));
  if (!identity) return out;
  saved.forEach((_name, key) => {
    if (written.has(key) || !key.startsWith(LINK_GROUP_KEY_PREFIX)) return;
    if (campaignLinkIdentity(key.slice(LINK_GROUP_KEY_PREFIX.length)) === identity) out.push(key);
  });
  return out;
}

/** 캠페인 문서의 `postGroups` → 키 → 그룹 이름(빈 이름은 뺀다 · «일부러 뺀 글»은 `CAMPAIGN_POST_GROUP_NONE` 그대로) */
export function readCampaignPostGroups(campaign: { postGroups?: unknown } | null | undefined): Map<string, string> {
  const map = new Map<string, string>();
  const raw = campaign?.postGroups;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const k = String(key || '').trim();
    const name = collapseGroupName(value) === CAMPAIGN_POST_GROUP_NONE ? CAMPAIGN_POST_GROUP_NONE : normalizeCampaignPostGroupName(value);
    if (k && name) map.set(k, name);
  });
  return map;
}

/**
 * 링크의 그룹 이름 — 그 링크의 링크 키(`campaignLinkGroupKey`)가 있으면 그것, 없으면 같은 글(`campaignLinkIdentity`)인 링크 키의 그룹
 * (같은 글의 링크 키가 여럿이면 맵의 앞 키). 캠페인 창 링크 칸과 표가 같이 쓴다.
 * @param groups `readCampaignPostGroups` 결과. ⚠️ 넘긴 뒤 바꾸지 않는다(열쇠 색인을 맵마다 한 번 만든다)
 * @returns 그룹이 없으면 `null`
 */
export function campaignLinkGroupOf(link: unknown, groups: ReadonlyMap<string, string>): string | null {
  const name = linkGroupRaw(link, groups);
  return name && name !== CAMPAIGN_POST_GROUP_NONE ? name : null;
}

/**
 * 글의 그룹 이름 — 글의 링크 키 → 같은 글인 링크 키 → id 키 → 글쓴 계정 키 차례(글에 정한 값이 계정 값을 이긴다).
 * 글 키의 값이 `CAMPAIGN_POST_GROUP_NONE` 이면(일부러 뺀 글) 거기서 멈추고 계정 그룹을 물려받지 않는다.
 * @returns 그룹이 없으면 `null`
 */
export function campaignPostGroupOf(post: any, groups: ReadonlyMap<string, string>): string | null {
  if (groups.size === 0) return null;
  const id = String(post?.id || '').trim();
  const postNames = [linkGroupRaw(post?.link || post?.url || '', groups), id ? groups.get(id) : undefined];
  for (const name of postNames) {
    if (name) return name === CAMPAIGN_POST_GROUP_NONE ? null : name;
  }
  for (const key of campaignPostAccountGroupKeys(post)) {
    const name = groups.get(key);
    if (name && name !== CAMPAIGN_POST_GROUP_NONE) return name;
  }
  return null;
}

/**
 * 고를 칸 — 이 캠페인에서 쓴 이름(처음 나온 차례). 묶음 차례도 이것을 따른다. 아무 그룹도 안 썼으면 빈 배열.
 * @param extra 아직 저장하지 않은 이름(방금 친 새 그룹 등)
 */
export function campaignPostGroupOptions(groups: ReadonlyMap<string, string>, extra: readonly string[] = []): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const name = normalizeCampaignPostGroupName(raw);
    if (!name || seen.has(name)) return;
    seen.add(name);
    out.push(name);
  };
  groups.forEach((name) => push(name));
  extra.forEach(push);
  return out;
}

/** 그룹 거르기 — 전체면 받은 배열 그대로(같은 참조) */
export function filterCampaignPostsByGroup<T>(
  posts: readonly T[],
  groups: ReadonlyMap<string, string>,
  filter: CampaignPostGroupFilter,
): readonly T[] {
  if (!filter || filter === 'all') return posts;
  return posts.filter((post) => {
    const name = campaignPostGroupOf(post, groups);
    return filter === CAMPAIGN_POST_GROUP_NONE ? !name : name === filter;
  });
}

/** 그룹 이름(「그룹 없음」은 `CAMPAIGN_POST_GROUP_NONE`) → 글 수 */
export function countCampaignPostsByGroup(posts: readonly unknown[], groups: ReadonlyMap<string, string>): Map<string, number> {
  const counts = new Map<string, number>();
  posts.forEach((post) => {
    const key = campaignPostGroupOf(post, groups) ?? CAMPAIGN_POST_GROUP_NONE;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return counts;
}

/** 글 한 벌의 숫자 — 상단 카드와 같은 셈이다. 회수율·예산은 계정 단위라 그룹에는 세지 않는다 */
export function summarizeCampaignPostGroup(posts: readonly CampaignPost[]): CampaignPostGroupSummary {
  const summary = summarizeCampaignPosts(posts);
  const parts = summarizeCampaignReactionParts(posts);
  return {
    postsCount: summary.postsCount,
    viewTotal: summary.viewTotal,
    reactionTotal: summary.reactionTotal,
    parts,
    engagementRate: engagementRatePct(parts.like, parts.comment, summary.viewTotal),
  };
}

/**
 * 그룹별로 묶는다 — 묶음 차례는 `campaignPostGroupOptions`, 「그룹 없음」은 맨 뒤. 글이 없는 그룹은 뺀다.
 * 묶음 안의 글은 받은 차례(정렬)를 지킨다.
 */
export function buildCampaignPostGroupSections<T extends CampaignPost>(
  posts: readonly T[],
  groups: ReadonlyMap<string, string>,
): CampaignPostGroupSection<T>[] {
  const byKey = new Map<string, T[]>();
  posts.forEach((post) => {
    const key = campaignPostGroupOf(post, groups) ?? CAMPAIGN_POST_GROUP_NONE;
    const bucket = byKey.get(key);
    if (bucket) bucket.push(post);
    else byKey.set(key, [post]);
  });
  const order = [...campaignPostGroupOptions(groups), ...[...byKey.keys()].filter((k) => k !== CAMPAIGN_POST_GROUP_NONE), CAMPAIGN_POST_GROUP_NONE];
  const seen = new Set<string>();
  const sections: CampaignPostGroupSection<T>[] = [];
  order.forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);
    const bucket = byKey.get(key);
    if (!bucket || bucket.length === 0) return;
    sections.push({
      key,
      name: key === CAMPAIGN_POST_GROUP_NONE ? CAMPAIGN_POST_GROUP_NONE_LABEL : key,
      posts: bucket,
      summary: summarizeCampaignPostGroup(bucket),
    });
  });
  return sections;
}

/** 링크 칸 한 줄의 그룹 — `diffCampaignLinkGroups` 의 재료 */
export type CampaignLinkGroupDraft = { link: string; group?: string | null };

/** 계정 칸 한 줄의 그룹 — `diffCampaignAccountGroups` 의 재료 */
export type CampaignAccountGroupDraft = { platform: string; account: string; group?: string | null };

/** 저장할 묶음 — 그룹마다 키 목록(`null` 은 빼기). `campaign.postGroups.set` 한 번이 한 묶음이다 */
export type CampaignGroupWrite = { group: string | null; keys: string[] };

/**
 * 링크 칸의 그룹 → 저장할 묶음(그룹마다 링크 키 목록 · `null` 은 빼기). 저장값과 같은 줄은 싣지 않는다.
 * @param saved 키 → 저장된 그룹(`readCampaignPostGroups`)
 */
export function diffCampaignLinkGroups(
  drafts: readonly CampaignLinkGroupDraft[],
  saved: ReadonlyMap<string, string>,
): CampaignGroupWrite[] {
  const entries: Array<[string, string | null | undefined]> = [];
  drafts.forEach((d) => {
    const key = campaignLinkGroupKey(d.link);
    // 링크 칸이 보인 값(같은 글인 다른 링크 키의 값까지 — `campaignLinkGroupOf`)과 같으면 손대지 않는다
    if (!key || normalizeCampaignPostGroupName(d.group) === (campaignLinkGroupOf(d.link, saved) ?? '')) return;
    entries.push([key, d.group]);
    // 같은 글인 다른 링크 키(표에서 글 링크 모양으로 적은 키 등)는 지운다 — 남으면 그 글에서 그 값이 새 값을 가린다
    staleCampaignPostGroupKeys({ link: d.link }, [key], saved).forEach((stale) => entries.push([stale, null]));
  });
  return diffGroupEntries(entries, saved);
}

/**
 * 계정 칸의 그룹 → 저장할 묶음. 저장값과 같은 줄은 싣지 않고, 목록에서 빠진 계정의 저장된 그룹은 뺀다
 * (빠진 계정의 그룹이 남으면 링크로 넣은 그 계정 글이 옛 그룹을 물려받는다).
 * @param saved 키 → 저장된 그룹(`readCampaignPostGroups`) — 복제·새 캠페인이면 빈 맵
 */
export function diffCampaignAccountGroups(
  drafts: readonly CampaignAccountGroupDraft[],
  saved: ReadonlyMap<string, string>,
): CampaignGroupWrite[] {
  const entries: Array<[string, string | null | undefined]> = drafts.map((d) => [campaignAccountGroupKey(d.platform, d.account), d.group]);
  const kept = new Set(entries.map(([key]) => key));
  saved.forEach((_, key) => {
    if (key.startsWith(CAMPAIGN_ACCOUNT_GROUP_KEY_PREFIX) && !kept.has(key)) entries.push([key, null]);
  });
  return diffGroupEntries(entries, saved);
}

const LINK_GROUP_KEY_PREFIX = 'link:';
const LINK_GROUP_INDEX_CACHE = new WeakMap<object, Map<string, string>>();

/** 앞뒤 공백 · 연속 공백 · 최대 글자 수만 다듬은 이름(쓸 수 없는 이름도 그대로) */
function collapseGroupName(raw: unknown): string {
  return String(raw ?? '').replace(/\s+/g, ' ').trim().slice(0, CAMPAIGN_POST_GROUP_NAME_MAX);
}

/** `_none`(«일부러 뺀 글» 값) · `__…__`(Firestore 예약 꼴) */
function isReservedGroupName(name: string): boolean {
  return name === CAMPAIGN_POST_GROUP_NONE || /^__.*__$/.test(name);
}

/** 링크의 저장값 그대로 — 링크 키 → 같은 글인 링크 키 차례 · `CAMPAIGN_POST_GROUP_NONE` 도 그대로 돌려준다 */
function linkGroupRaw(link: unknown, groups: ReadonlyMap<string, string>): string | null {
  if (groups.size === 0) return null;
  const key = campaignLinkGroupKey(link);
  if (!key) return null;
  const exact = groups.get(key);
  if (exact) return exact;
  const identity = campaignLinkIdentity(String(link ?? ''));
  return identity ? linkGroupIdentityIndex(groups).get(identity) ?? null : null;
}

/** 저장된 링크 키(`link:` + 링크)의 «같은 글» 열쇠 → 그룹 이름(같은 열쇠는 앞 키) */
function linkGroupIdentityIndex(groups: ReadonlyMap<string, string>): Map<string, string> {
  const cached = LINK_GROUP_INDEX_CACHE.get(groups);
  if (cached) return cached;
  const index = new Map<string, string>();
  groups.forEach((name, key) => {
    if (!key.startsWith(LINK_GROUP_KEY_PREFIX)) return;
    const identity = campaignLinkIdentity(key.slice(LINK_GROUP_KEY_PREFIX.length));
    if (identity && !index.has(identity)) index.set(identity, name);
  });
  LINK_GROUP_INDEX_CACHE.set(groups, index);
  return index;
}

/** 키·새 그룹 쌍 → 저장값과 다른 것만 그룹마다 묶는다(빈 키는 버린다) */
function diffGroupEntries(
  entries: ReadonlyArray<readonly [string, string | null | undefined]>,
  saved: ReadonlyMap<string, string>,
): CampaignGroupWrite[] {
  const byGroup = new Map<string, string[]>();
  const seen = new Set<string>();
  entries.forEach(([key, group]) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    const next = normalizeCampaignPostGroupName(group);
    const stored = saved.get(key) ?? '';
    const prev = stored === CAMPAIGN_POST_GROUP_NONE ? '' : stored;
    if (next === prev) return;
    const bucket = byGroup.get(next);
    if (bucket) bucket.push(key);
    else byGroup.set(next, [key]);
  });
  return [...byGroup.entries()].map(([group, keys]) => ({ group: group || null, keys }));
}
